import { z } from "zod";
import { ProviderModelIdSchema } from "./providerConfig.js";
import { normalizeEvidenceSourceTextReadings, normalizeEvidenceText } from "./evidenceText.js";
import { ApartmentTakeoffRowSchema, ENGINE_SAFE_TAKEOFF_RAW_FIELDS_KEY, EngineSafeTakeoffProjectionSchema, HeadingMapEntrySchema, TakeoffWarningSchema } from "./takeoff.js";
import { ConfidenceSchema, IdSchema, IsoTimestampSchema, NonEmptyStringSchema, NonNegativeIntegerSchema, PositiveIntegerSchema, RegionRefSchema, SourceRefSchema, LabeledDrawingEvidenceSchema, UnknownRecordSchema } from "./source.js";
export const TENDER_INTAKE_SCHEMA_VERSION = "tender-intake.v1";
export const TENDER_INTAKE_ARTIFACT_VERSION = "tender-intake-artifact-v1";
export const TENDER_INTAKE_RUBRIC_VERSION = "tender-classification-rubric-v1";
export const TENDER_TAKEOFF_SCHEMA_VERSION = "wyvil-pure-physical-v1";
export const TENDER_TAKEOFF_ADAPTER_VERSION = "tender-takeoff-adapter-v1";
export const TENDER_SCHEDULE_ADAPTER_VERSION = "tender-schedule-adapter-v1";
export const TENDER_TAKEOFF_PURE_PHYSICAL_ERROR_CODE = "TENDER_TAKEOFF_PURE_PHYSICAL_VIOLATION";
export const TENDER_TAKEOFF_UNRESOLVED_CONFLICT_ERROR_CODE = "TENDER_TAKEOFF_UNRESOLVED_CONFLICT";
export const TENDER_TAKEOFF_TILE_REF_ERROR_CODE = "TENDER_TAKEOFF_TILE_REF_UNRESOLVED";
export const TenderStageSchema = z.enum([
    "upload",
    "unzip",
    "manifest",
    "classify",
    "parse",
    "requirement-crawl",
    "takeoff",
    "engine-feed",
    "delete"
]);
export const TenderStageStatusSchema = z.enum([
    "queued",
    "running",
    "succeeded",
    "failed",
    "blocked",
    "waived",
    "cancelled",
    "superseded",
    "deleted"
]);
export const TenderActorSchema = z
    .object({
    actorType: z.enum(["user", "agent", "tool", "system"]),
    actorId: NonEmptyStringSchema.optional(),
    model: ProviderModelIdSchema.optional(),
    tool: NonEmptyStringSchema.optional(),
    promptVersion: NonEmptyStringSchema.optional(),
    runId: IdSchema.optional()
})
    .strict();
export const TenderObjectRefSchema = z
    .object({
    bucket: NonEmptyStringSchema,
    path: NonEmptyStringSchema,
    generation: NonEmptyStringSchema,
    sizeBytes: NonNegativeIntegerSchema,
    sha256: NonEmptyStringSchema,
    contentType: NonEmptyStringSchema
})
    .strict();
export const TenderProvenanceSchema = z
    .object({
    sourceRefs: z.array(SourceRefSchema).optional(),
    objectRefs: z.array(TenderObjectRefSchema).optional(),
    runIds: z.array(IdSchema).optional(),
    inputArtifactIds: z.array(IdSchema).optional(),
    sourceObjectGenerations: z.record(NonEmptyStringSchema).optional(),
    legacyMode: z.literal("manual_text_legacy").optional(),
    legacyDocumentId: IdSchema.optional(),
    metadata: UnknownRecordSchema.optional()
})
    .strict();
const TenderCommonEntityFieldsSchema = z
    .object({
    id: IdSchema,
    version: PositiveIntegerSchema,
    schemaVersion: z.literal(TENDER_INTAKE_SCHEMA_VERSION),
    orgId: IdSchema,
    projectId: IdSchema,
    tenderId: IdSchema,
    packId: IdSchema,
    stage: TenderStageSchema,
    stageStatus: TenderStageStatusSchema,
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
    createdBy: TenderActorSchema,
    provenance: TenderProvenanceSchema,
    idempotencyKey: NonEmptyStringSchema,
    inputFingerprint: NonEmptyStringSchema,
    artifactRevisionId: IdSchema,
    previousArtifactRevisionId: IdSchema.optional(),
    replayOfRunId: IdSchema.optional(),
    supersededById: IdSchema.optional(),
    duplicateOfPackId: IdSchema.optional(),
    duplicateFinalizeInputFingerprint: NonEmptyStringSchema.optional(),
    deletedAt: IsoTimestampSchema.optional()
})
    .strict();
export const TenderPackSourceKindSchema = z.enum([
    "zip_archive",
    "single_file",
    "multi_file_batch",
    "manual_paste_pack",
    "pasted_text"
]);
export const TenderUploadStateSchema = z.enum([
    "draft",
    "uploading",
    "uploaded",
    "finalize_requested",
    "finalized",
    "cancelled"
]);
export const TenderRetentionStateSchema = z.enum([
    "active",
    "delete_requested",
    "delete_in_progress",
    "deleted",
    "legal_hold"
]);
export const TenderPackCountsSchema = z
    .object({
    archiveEntries: NonNegativeIntegerSchema,
    acceptedFiles: NonNegativeIntegerSchema,
    rejectedEntries: NonNegativeIntegerSchema,
    unsupportedFiles: NonNegativeIntegerSchema,
    encryptedEntries: NonNegativeIntegerSchema,
    documents: NonNegativeIntegerSchema,
    duplicateDocuments: NonNegativeIntegerSchema,
    loadBearingCadBimBlocks: NonNegativeIntegerSchema
})
    .strict();
export const TenderPackSchema = TenderCommonEntityFieldsSchema.extend({
    packName: NonEmptyStringSchema,
    sourceKind: TenderPackSourceKindSchema,
    uploadState: TenderUploadStateSchema,
    archiveObjectRef: TenderObjectRefSchema.optional(),
    uploadSession: z
        .object({
        bucket: NonEmptyStringSchema,
        objectPath: NonEmptyStringSchema,
        uploadUrl: NonEmptyStringSchema.optional(),
        uploadMethod: z.literal("gcs-resumable"),
        contentType: NonEmptyStringSchema,
        contentLength: PositiveIntegerSchema,
        headers: z.record(NonEmptyStringSchema).optional()
    })
        .strict()
        .optional(),
    rootPrefix: NonEmptyStringSchema,
    counts: TenderPackCountsSchema,
    activeStageRunId: IdSchema.optional(),
    /**
     * The id the EngineRunCoordinator generated for this pack's current (or
     * most recent) engine chain run -- distinct from activeStageRunId, which
     * is the legacy per-stage worker-trigger run id and is NEVER the id the
     * coordinator's own durable event feed is keyed by (engineRunCoordinator.ts
     * mints `engine-${randomUUID()}`). Consumers that need "this pack's
     * current engine run" must read this field first, falling back to
     * activeStageRunId only for legacy rows recorded before this field
     * existed. Nullable/omittable: absent until an engine run has actually
     * been started for this pack; superseded on every subsequent run start.
     */
    engineRunId: IdSchema.optional(),
    overrideSetVersion: NonNegativeIntegerSchema,
    retentionState: TenderRetentionStateSchema,
    auditEventIds: z.array(IdSchema)
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.sourceKind === "manual_paste_pack" &&
        value.provenance.legacyMode !== "manual_text_legacy") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'manual_paste_pack requires provenance.legacyMode = "manual_text_legacy"',
            path: ["provenance", "legacyMode"]
        });
    }
});
export const TenderArchiveSafetyOutcomeSchema = z.enum([
    "accepted",
    "accepted_duplicate",
    "rejected_unsafe_path",
    "rejected_symlink",
    "rejected_hardlink",
    "rejected_device",
    "rejected_archive_depth",
    "rejected_file_count_cap",
    "rejected_per_file_size_cap",
    "rejected_total_uncompressed_cap",
    "rejected_compression_ratio_cap",
    "zip_entry_corrupt",
    "single_file_object_copy_failed",
    "encrypted_archive",
    "unsupported_file_type",
    "unsupported_archive_type",
    "quarantined_scan_pending",
    "quarantined_scan_failed"
]);
export const TenderArchiveEntryTypeSchema = z.enum([
    "file",
    "directory",
    "symlink",
    "hardlink",
    "device",
    "archive",
    "unsupported"
]);
const NestedArchiveChainItemSchema = z
    .object({
    archiveEntryId: IdSchema,
    normalizedName: NonEmptyStringSchema
})
    .strict();
export const TenderArchiveEntrySchema = TenderCommonEntityFieldsSchema.extend({
    archiveEntryId: IdSchema,
    parentArchiveEntryId: IdSchema.optional(),
    nestedArchiveChain: z.array(NestedArchiveChainItemSchema),
    rawPath: NonEmptyStringSchema,
    normalizedRelPath: NonEmptyStringSchema,
    entryType: TenderArchiveEntryTypeSchema,
    depth: NonNegativeIntegerSchema,
    compressedSizeBytes: NonNegativeIntegerSchema.optional(),
    uncompressedSizeBytes: NonNegativeIntegerSchema.optional(),
    compressionRatio: z.number().nonnegative().optional(),
    declaredContentType: NonEmptyStringSchema.optional(),
    sniffedContentType: NonEmptyStringSchema.optional(),
    sha256: NonEmptyStringSchema.optional(),
    safetyOutcome: TenderArchiveSafetyOutcomeSchema,
    quarantineObjectRef: TenderObjectRefSchema.optional(),
    acceptedObjectRef: TenderObjectRefSchema.optional()
}).strict();
export const TenderDocumentRegistryRowSchema = TenderCommonEntityFieldsSchema.extend({
    docId: IdSchema,
    archiveEntryId: IdSchema,
    objectRef: TenderObjectRefSchema,
    originalFilename: NonEmptyStringSchema,
    originalRelPath: NonEmptyStringSchema,
    normalizedRelPath: NonEmptyStringSchema,
    extension: NonEmptyStringSchema,
    sniffedContentType: NonEmptyStringSchema,
    sha256: NonEmptyStringSchema,
    dedupeKey: NonEmptyStringSchema,
    duplicateFamilyId: IdSchema.optional(),
    duplicateOfDocId: IdSchema.optional(),
    revisionFamilyId: IdSchema.optional(),
    revisionId: IdSchema.optional(),
    // Legacy registry rows predate the human-decision guard. Missing is version 0;
    // every user override must persist a strictly higher document state version.
    stateVersion: NonNegativeIntegerSchema.optional(),
    revisionLabel: NonEmptyStringSchema.optional(),
    revisionDate: IsoTimestampSchema.optional(),
    revisionRank: NonNegativeIntegerSchema.optional(),
    supersedesDocIds: z.array(IdSchema),
    supersededByDocId: IdSchema.optional(),
    documentNumber: NonEmptyStringSchema.optional(),
    discipline: NonEmptyStringSchema.optional(),
    blockRefs: z.array(NonEmptyStringSchema),
    tenureRefs: z.array(NonEmptyStringSchema),
    apartmentTypeRefs: z.array(NonEmptyStringSchema),
    manifestStatus: z.enum(["pending", "complete", "failed", "unsupported"]),
    manifestFailure: z
        .object({
        code: NonEmptyStringSchema,
        message: NonEmptyStringSchema,
        retryable: z.boolean()
    })
        .strict()
        .optional(),
    classificationStatus: z.enum([
        "pending",
        "included",
        "excluded",
        "unsure",
        "overridden",
        "failed"
    ]),
    parseStatus: z.enum([
        "not_required",
        "pending",
        "running",
        "complete",
        "failed",
        "blocked"
    ]),
    loadBearingStatus: z.enum(["unknown", "load_bearing", "non_load_bearing", "waived"]),
    cadBimStatus: z.enum([
        "not_cad_bim",
        "unsupported_present",
        "pdf_equivalent_found",
        "blocked_without_waiver",
        "waived"
    ]),
    deleteState: z.enum([
        "active",
        "delete_requested",
        "object_deleted",
        "metadata_deleted"
    ])
}).strict();
export const ManifestDocumentRowSchema = z
    .object({
    docId: IdSchema,
    pageCount: NonNegativeIntegerSchema.optional(),
    hasTextLayer: z.boolean().optional(),
    firstPageTextExcerpt: NonEmptyStringSchema.optional(),
    excerptHash: NonEmptyStringSchema.optional(),
    titleBlockText: NonEmptyStringSchema.optional(),
    sheetNames: z.array(NonEmptyStringSchema).optional(),
    drawingNumberCandidates: z.array(NonEmptyStringSchema),
    revisionCandidates: z.array(NonEmptyStringSchema),
    folderDisciplineSignal: NonEmptyStringSchema.optional(),
    filenameDisciplineSignal: NonEmptyStringSchema.optional(),
    cadBimCompanionCandidates: z.array(IdSchema),
    manifestWarnings: z.array(NonEmptyStringSchema)
})
    .strict();
export const ManifestArtifactSchema = TenderCommonEntityFieldsSchema.extend({
    artifactId: IdSchema,
    manifestTool: NonEmptyStringSchema,
    toolVersion: NonEmptyStringSchema,
    documentRows: z.array(ManifestDocumentRowSchema),
    coverage: UnknownRecordSchema,
    dwgBimBlocking: z.array(IdSchema)
}).strict();
export const ClassificationDecisionSchema = z.enum(["INCLUDE", "EXCLUDE", "UNSURE"]);
export const ClassificationEffectiveDecisionSchema = z.enum(["INCLUDE", "EXCLUDE"]);
export const ClassificationTraversalTurnSchema = z
    .object({
    agentTurnId: IdSchema,
    selectedTools: z.array(NonEmptyStringSchema),
    argsSummary: NonEmptyStringSchema,
    outcome: NonEmptyStringSchema,
    elapsedMs: NonNegativeIntegerSchema
})
    .strict();
export const ClassificationTraversalSchema = z
    .object({
    mode: z.enum(["single-shot", "multi-turn"]),
    pageCount: NonNegativeIntegerSchema.optional(),
    maxTurns: PositiveIntegerSchema,
    turns: z.array(ClassificationTraversalTurnSchema),
    exhausted: z.boolean(),
    exhaustionReason: NonEmptyStringSchema.optional()
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.exhausted && !value.exhaustionReason) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "exhausted traversal requires exhaustionReason",
            path: ["exhaustionReason"]
        });
    }
});
export const ClassificationArtifactRowSchema = z
    .object({
    docId: IdSchema,
    docKey: NonEmptyStringSchema,
    sha256: NonEmptyStringSchema,
    normalizedRelPath: NonEmptyStringSchema,
    decision: ClassificationDecisionSchema,
    effectiveDecision: ClassificationEffectiveDecisionSchema,
    overrideEventId: IdSchema.optional(),
    confidence: ConfidenceSchema,
    discipline: NonEmptyStringSchema,
    docType: z.enum([
        "drawing",
        "specification",
        "schedule",
        "scope",
        "correspondence",
        "commercial",
        "information",
        "unrelated",
        "unsupported"
    ]),
    docSubtype: NonEmptyStringSchema.optional(),
    apartmentTypeRefs: z.array(NonEmptyStringSchema),
    blockRefs: z.array(NonEmptyStringSchema),
    tenureRefs: z.array(NonEmptyStringSchema),
    revisionFamilyId: IdSchema.optional(),
    revisionId: IdSchema.optional(),
    duplicateFamilyId: IdSchema.optional(),
    reason: NonEmptyStringSchema,
    evidenceRefs: z.array(SourceRefSchema),
    requiresReview: z.boolean(),
    codeGuardFlags: z.array(NonEmptyStringSchema),
    traversal: ClassificationTraversalSchema.optional()
})
    .strict();
export const ClassificationArtifactSchema = TenderCommonEntityFieldsSchema.extend({
    artifactId: IdSchema,
    rubricVersion: z.literal(TENDER_INTAKE_RUBRIC_VERSION),
    modelId: ProviderModelIdSchema,
    rows: z.array(ClassificationArtifactRowSchema),
    includedDocIds: z.array(IdSchema),
    excludedDocIds: z.array(IdSchema),
    unsureDocIds: z.array(IdSchema),
    costLedgerEntries: z.array(z.lazy(() => TenderCostLedgerEntrySchema)),
    classificationSetFingerprint: NonEmptyStringSchema
}).strict();
export const ClassificationOverrideEventSchema = TenderCommonEntityFieldsSchema.extend({
    eventId: IdSchema,
    docId: IdSchema,
    priorDecision: ClassificationDecisionSchema,
    newDecision: z.enum(["FORCE_INCLUDE", "FORCE_EXCLUDE", "CLEAR_OVERRIDE"]),
    reason: NonEmptyStringSchema,
    actor: z
        .object({
        userId: IdSchema,
        displayLabel: NonEmptyStringSchema
    })
        .strict(),
    overrideSetVersionBefore: NonNegativeIntegerSchema,
    overrideSetVersionAfter: PositiveIntegerSchema,
    invalidates: z.array(NonEmptyStringSchema),
    appliesToRevisionFamily: z.boolean(),
    appliesToDuplicateFamily: z.boolean()
}).strict();
const DeterministicToolOutputSchema = z
    .object({
    tool: NonEmptyStringSchema,
    toolVersion: NonEmptyStringSchema,
    objectRef: TenderObjectRefSchema.optional(),
    textHash: NonEmptyStringSchema.optional(),
    tableHash: NonEmptyStringSchema.optional(),
    warnings: z.array(NonEmptyStringSchema)
})
    .strict();
const RenderObjectSchema = z
    .object({
    renderObjectId: IdSchema,
    docId: IdSchema,
    page: PositiveIntegerSchema,
    tileId: IdSchema.optional(),
    objectRef: TenderObjectRefSchema,
    widthPx: PositiveIntegerSchema,
    heightPx: PositiveIntegerSchema,
    region: RegionRefSchema.optional(),
    renderTool: NonEmptyStringSchema,
    renderVersion: NonEmptyStringSchema
})
    .strict();
const TextSpanSchema = z
    .object({
    spanId: IdSchema,
    page: PositiveIntegerSchema.optional(),
    sheetName: NonEmptyStringSchema.optional(),
    cellRange: NonEmptyStringSchema.optional(),
    text: NonEmptyStringSchema,
    sourceRef: SourceRefSchema,
    confidence: ConfidenceSchema
})
    .strict();
const ParseTableSchema = z
    .object({
    tableId: IdSchema,
    page: PositiveIntegerSchema.optional(),
    sheetName: NonEmptyStringSchema.optional(),
    rowsObjectRef: TenderObjectRefSchema.optional(),
    sourceRef: SourceRefSchema,
    confidence: ConfidenceSchema
})
    .strict();
const ParseChannelDisagreementSchema = z
    .object({
    field: NonEmptyStringSchema,
    summary: NonEmptyStringSchema,
    nativeEvidenceRefs: z.array(SourceRefSchema),
    toolEvidenceRefs: z.array(SourceRefSchema),
    severity: z.enum(["info", "warning", "blocker"])
})
    .strict();
const ParseChannelComparisonSchema = z
    .object({
    nativeVsToolAgreement: z.enum([
        "agreement",
        "partial_agreement",
        "disagreement",
        "not_applicable"
    ]),
    disagreements: z.array(ParseChannelDisagreementSchema),
    targetedRelookIds: z.array(IdSchema)
})
    .strict();
const ParsePageCoverageRowSchema = z
    .object({
    pageRange: NonEmptyStringSchema,
    visitKind: z.enum(["agent-native", "vision-render", "tool-text", "crawl"]),
    agentTurnId: IdSchema.optional(),
    coveredAt: IsoTimestampSchema
})
    .strict();
const ParseTraversalPlanSchema = z
    .object({
    strategy: z.enum(["section", "page-window", "whole"]),
    windows: z.array(z
        .object({
        pages: NonEmptyStringSchema,
        sectionRef: NonEmptyStringSchema.optional()
    })
        .strict())
})
    .strict();
export const ParseArtifactSchema = TenderCommonEntityFieldsSchema.extend({
    artifactId: IdSchema,
    docId: IdSchema,
    parseRoute: z.enum([
        "native-document-agent",
        "vision-render-agent",
        "spreadsheet-tool-plus-agent",
        "email-tool-plus-agent",
        "unsupported"
    ]),
    modelId: ProviderModelIdSchema,
    nativeInputObjectRef: TenderObjectRefSchema.optional(),
    deterministicToolOutputs: z.array(DeterministicToolOutputSchema),
    renderObjects: z.array(RenderObjectSchema),
    textSpans: z.array(TextSpanSchema),
    tables: z.array(ParseTableSchema),
    entities: UnknownRecordSchema,
    channelComparison: ParseChannelComparisonSchema,
    verificationFlags: z.array(NonEmptyStringSchema),
    parseCompleteness: z.enum(["complete", "partial", "blocked", "waived"]),
    pageCoverage: z.array(ParsePageCoverageRowSchema),
    uncoveredPages: z.array(PositiveIntegerSchema),
    traversalPlan: ParseTraversalPlanSchema.optional()
}).strict();
export const RequirementClaimedProvenanceSchema = z
    .object({
    page: PositiveIntegerSchema.optional(),
    spanId: IdSchema.optional(),
    tableId: IdSchema.optional(),
    sheetName: NonEmptyStringSchema.optional(),
    cellRef: NonEmptyStringSchema.optional(),
    rangeRef: NonEmptyStringSchema.optional()
})
    .strict();
export const RequirementProvenanceIssueCodeSchema = z.enum([
    "claimed_locator_not_found",
    "verbatim_not_found",
    "verbatim_ambiguous",
    "source_quote_not_specific",
    "provider_schema_invalid"
]);
const RequirementUnresolvedProvenanceFieldsSchema = z
    .object({
    rowId: IdSchema,
    requirement: NonEmptyStringSchema,
    verbatim: NonEmptyStringSchema,
    sourceDocId: IdSchema,
    claimedProvenance: RequirementClaimedProvenanceSchema,
    provenanceStatus: z.literal("provenance_unresolved"),
    provenanceIssueCode: RequirementProvenanceIssueCodeSchema,
    provenanceIssue: NonEmptyStringSchema,
    sectionCode: NonEmptyStringSchema.optional(),
    page: PositiveIntegerSchema.optional(),
    discipline: NonEmptyStringSchema,
    scopeTag: NonEmptyStringSchema,
    fixStage: NonEmptyStringSchema.optional(),
    obligation: NonEmptyStringSchema.optional(),
    responsibleParty: NonEmptyStringSchema.optional(),
    brandOrStandard: NonEmptyStringSchema.optional(),
    cablingNote: NonEmptyStringSchema.optional(),
    likelyExclude: z.boolean().optional(),
    excludeReason: NonEmptyStringSchema.optional(),
    pricingImpact: NonEmptyStringSchema.optional(),
    confidence: ConfidenceSchema,
    candidateF2EvidenceGroups: z.array(z.enum(["A", "B", "C", "D", "E", "F", "G", "H"]))
})
    .strict();
export const RequirementUnresolvedCrawlRowSchema = RequirementUnresolvedProvenanceFieldsSchema;
export const RequirementCrawlRowSchema = z
    .object({
    rowId: IdSchema,
    requirement: NonEmptyStringSchema,
    verbatim: NonEmptyStringSchema,
    sourceDocId: IdSchema,
    sourceRef: SourceRefSchema,
    sectionCode: NonEmptyStringSchema.optional(),
    page: PositiveIntegerSchema.optional(),
    discipline: NonEmptyStringSchema,
    scopeTag: NonEmptyStringSchema,
    fixStage: NonEmptyStringSchema.optional(),
    obligation: NonEmptyStringSchema.optional(),
    responsibleParty: NonEmptyStringSchema.optional(),
    brandOrStandard: NonEmptyStringSchema.optional(),
    cablingNote: NonEmptyStringSchema.optional(),
    likelyExclude: z.boolean().optional(),
    excludeReason: NonEmptyStringSchema.optional(),
    pricingImpact: NonEmptyStringSchema.optional(),
    confidence: ConfidenceSchema,
    candidateF2EvidenceGroups: z.array(z.enum(["A", "B", "C", "D", "E", "F", "G", "H"]))
})
    .strict()
    .superRefine((value, ctx) => {
    if (!sourceRefQuoteSupportsVerbatim(value.sourceRef, value.verbatim)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "requirement rows require a sourceRef quote matching verbatim",
            path: ["sourceRef", "quote"]
        });
    }
    if (value.sourceRef.sourceId !== value.sourceDocId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "requirement sourceRef.sourceId must match sourceDocId",
            path: ["sourceRef", "sourceId"]
        });
    }
});
export const RequirementCrawlArtifactSchema = TenderCommonEntityFieldsSchema.extend({
    artifactId: IdSchema,
    crawlLens: z.enum([
        "mechanical-review",
        "controls-bms-review",
        "scope-demarcation",
        "commercial-compliance",
        "generalist-sweep"
    ]),
    sourceDocIds: z.array(IdSchema),
    coverage: z
        .object({
        includeDocIdsRead: z.array(IdSchema),
        includeDocIdsUnread: z.array(IdSchema),
        chunksRead: NonNegativeIntegerSchema,
        chunksSkipped: NonNegativeIntegerSchema
    })
        .strict(),
    rows: z.array(RequirementCrawlRowSchema),
    unresolvedRows: z.array(RequirementUnresolvedCrawlRowSchema).default([]),
    criticFlags: z.array(NonEmptyStringSchema)
}).strict();
export const FCU_SYSTEM_TYPE_RESEARCH_VERSION = "fcu-system-type-research.v1";
export const FCU_SYSTEM_TYPE_RESEARCH_DIRECTION_VERSION = "fcu-system-type-direction.v1";
export const FcuSystemTypePipeArrangementSchema = z.enum([
    "two_pipe",
    "four_pipe",
    "unknown"
]);
export const FcuSystemTypeThermalModeSchema = z.enum([
    "heating_cooling",
    "heat_only",
    "cool_only",
    "unknown"
]);
export const FcuSystemTypeValueSchema = z
    .object({
    pipeArrangement: FcuSystemTypePipeArrangementSchema,
    thermalMode: FcuSystemTypeThermalModeSchema
})
    .strict();
export const FcuSystemTypeEvidenceValueMapSchema = z
    .object({
    pipeArrangement: FcuSystemTypePipeArrangementSchema.optional(),
    thermalMode: FcuSystemTypeThermalModeSchema.optional()
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.pipeArrangement === undefined && value.thermalMode === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "FCU system-type evidence maps must bind at least one system-type field",
            path: ["pipeArrangement"]
        });
    }
});
export const FcuSystemTypeEvidenceKindSchema = z.enum([
    "mechanical_schedule",
    "fcu_schedule",
    "pipework_drawing",
    "mechanical_spec_section",
    "controls_spec_section",
    "prior_delivered_tender",
    "catalog_or_policy",
    "other"
]);
export const FcuSystemTypeProjectScopeSchema = z.enum([
    "same_project",
    "prior_delivered_tender",
    "catalog_or_policy"
]);
export const FcuSystemTypeAuthorityTierSchema = z.enum([
    "tender",
    "catalog",
    "policy",
    "exemplar"
]);
export const FcuSystemTypeEvidenceRoleSchema = z.enum([
    "decisive_same_project",
    "supporting_same_project",
    "supporting_exemplar",
    "advisory_catalog_or_policy",
    "rejected"
]);
export const FcuSystemTypeFieldRelevanceSchema = z.enum(["direct", "indirect", "none"]);
export const FcuSystemTypeEvidenceFindingSchema = z
    .object({
    evidenceId: IdSchema,
    sourceRef: SourceRefSchema,
    evidenceKind: FcuSystemTypeEvidenceKindSchema,
    projectScope: FcuSystemTypeProjectScopeSchema,
    authorityTier: FcuSystemTypeAuthorityTierSchema,
    evidenceRole: FcuSystemTypeEvidenceRoleSchema,
    fieldRelevance: FcuSystemTypeFieldRelevanceSchema,
    signal: z.enum(["presence", "omission"]),
    valueMap: FcuSystemTypeEvidenceValueMapSchema.optional(),
    statement: NonEmptyStringSchema
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.evidenceRole === "decisive_same_project") {
        if (value.projectScope !== "same_project") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "decisive FCU system-type evidence must be same-project evidence",
                path: ["projectScope"]
            });
        }
        if (value.authorityTier !== "tender" ||
            value.sourceRef.sourceKind === "benchmark" ||
            value.sourceRef.authorityClass === "benchmark") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "prior delivered tenders, catalog, and policy evidence are never decisive for FCU system type",
                path: ["authorityTier"]
            });
        }
        if (!value.valueMap) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "decisive FCU system-type evidence requires an evidence-to-value map",
                path: ["valueMap"]
            });
        }
    }
    if (value.evidenceRole === "supporting_exemplar") {
        if (value.projectScope !== "prior_delivered_tender" ||
            value.authorityTier !== "exemplar") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "exemplar FCU system-type evidence must be marked as prior-delivered-tender support",
                path: ["projectScope"]
            });
        }
        if (value.sourceRef.sourceKind !== "benchmark" &&
            value.sourceRef.authorityClass !== "benchmark") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "exemplar FCU system-type evidence must cite benchmark source provenance",
                path: ["sourceRef", "sourceKind"]
            });
        }
    }
    if (value.evidenceRole === "advisory_catalog_or_policy" &&
        value.fieldRelevance === "none") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "catalog or policy FCU system-type advice must disclose direct or indirect field relevance",
            path: ["fieldRelevance"]
        });
    }
});
export const FcuSystemTypeConclusionCandidateSchema = z
    .object({
    value: FcuSystemTypeValueSchema,
    evidenceIds: z.array(IdSchema),
    why: NonEmptyStringSchema,
    confidence: ConfidenceSchema
})
    .strict();
export const FcuSystemTypeResearchConclusionSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("concluded"),
        value: FcuSystemTypeValueSchema,
        decisiveEvidenceIds: z.array(IdSchema),
        supportingEvidenceIds: z.array(IdSchema),
        why: NonEmptyStringSchema,
        confidence: ConfidenceSchema
    })
        .strict(),
    z
        .object({
        kind: z.literal("needs_user_decision"),
        reason: z.enum([
            "decisive_same_project_evidence_absent",
            "same_authority_conflict",
            "exemplar_only",
            "verifier_blocker",
            "ambiguous_evidence"
        ]),
        candidateValues: z.array(FcuSystemTypeConclusionCandidateSchema),
        supportingExemplarEvidenceIds: z.array(IdSchema),
        blockerFindings: z.array(NonEmptyStringSchema),
        why: NonEmptyStringSchema,
        confidence: ConfidenceSchema
    })
        .strict()
]);
export const FcuSystemTypeToolCallTraceSchema = z
    .object({
    toolCallId: IdSchema,
    tool: NonEmptyStringSchema,
    argsSummary: NonEmptyStringSchema,
    outcome: NonEmptyStringSchema,
    elapsedMs: NonNegativeIntegerSchema
})
    .strict();
export const FcuSystemTypeVerifierFindingSchema = z
    .object({
    code: NonEmptyStringSchema,
    severity: z.enum(["info", "warning", "blocker"]),
    message: NonEmptyStringSchema,
    evidenceIds: z.array(IdSchema)
})
    .strict();
export const FcuSystemTypeSharedT3ContractSchema = z
    .object({
    crawlLens: z.literal("mechanical-review"),
    requirementCrawlArtifactIds: z.array(IdSchema),
    requirementRegisterRowIds: z.array(IdSchema),
    contractNote: NonEmptyStringSchema
})
    .strict();
export const FcuSystemTypeResearchArtifactSchema = TenderCommonEntityFieldsSchema.extend({
    artifactId: IdSchema,
    researchVersion: z.literal(FCU_SYSTEM_TYPE_RESEARCH_VERSION),
    directionVersion: z.literal(FCU_SYSTEM_TYPE_RESEARCH_DIRECTION_VERSION),
    sharedT3Contract: FcuSystemTypeSharedT3ContractSchema,
    sourceDocIds: z.array(IdSchema),
    evidence: z.array(FcuSystemTypeEvidenceFindingSchema),
    conclusion: FcuSystemTypeResearchConclusionSchema,
    toolCalls: z.array(FcuSystemTypeToolCallTraceSchema),
    verifierFindings: z.array(FcuSystemTypeVerifierFindingSchema)
}).strict();
export const REQUIREMENT_REGISTER_VERSION = "requirement-register.v1";
export const REQUIREMENT_CRITIC_VERSION = "requirement-critic.v1";
export const RequirementRegisterStatusSchema = z.enum([
    "proposed",
    "accepted",
    "rejected",
    "superseded"
]);
export const RequirementRegisterReviewEventSchema = z
    .object({
    eventId: IdSchema,
    action: z.enum(["review", "reapproved"]).optional(),
    reviewedAt: IsoTimestampSchema,
    reviewedBy: z
        .object({
        userId: IdSchema,
        displayLabel: NonEmptyStringSchema
    })
        .strict(),
    fromStatus: RequirementRegisterStatusSchema,
    toStatus: RequirementRegisterStatusSchema,
    reason: NonEmptyStringSchema.optional()
})
    .strict();
export const RequirementRegisterRowSchema = z
    .object({
    rowId: IdSchema,
    requirement: NonEmptyStringSchema,
    verbatim: NonEmptyStringSchema,
    sourceDocId: IdSchema,
    sourceRef: SourceRefSchema,
    sectionCode: NonEmptyStringSchema.optional(),
    page: PositiveIntegerSchema.optional(),
    discipline: NonEmptyStringSchema,
    scopeTag: NonEmptyStringSchema,
    fixStage: NonEmptyStringSchema.optional(),
    obligation: NonEmptyStringSchema.optional(),
    responsibleParty: NonEmptyStringSchema.optional(),
    brandOrStandard: NonEmptyStringSchema.optional(),
    cablingNote: NonEmptyStringSchema.optional(),
    likelyExclude: z.boolean().optional(),
    excludeReason: NonEmptyStringSchema.optional(),
    pricingImpact: NonEmptyStringSchema.optional(),
    confidence: ConfidenceSchema,
    candidateF2EvidenceGroups: z.array(z.enum(["A", "B", "C", "D", "E", "F", "G", "H"])),
    status: RequirementRegisterStatusSchema,
    contentFingerprint: NonEmptyStringSchema,
    materialRevisionOfRowId: IdSchema.optional(),
    supersededByRowId: IdSchema.optional(),
    sourceCrawlArtifactId: IdSchema,
    crawlLens: z.enum([
        "mechanical-review",
        "controls-bms-review",
        "scope-demarcation",
        "commercial-compliance",
        "generalist-sweep"
    ]),
    dedupeDisclosure: NonEmptyStringSchema.optional(),
    reviewEvents: z.array(RequirementRegisterReviewEventSchema)
})
    .strict()
    .superRefine((value, ctx) => {
    if (!sourceRefQuoteSupportsVerbatim(value.sourceRef, value.verbatim)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "register rows require a sourceRef quote matching verbatim",
            path: ["sourceRef", "quote"]
        });
    }
    if (value.sourceRef.sourceId !== value.sourceDocId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "register sourceRef.sourceId must match sourceDocId",
            path: ["sourceRef", "sourceId"]
        });
    }
    if (value.status === "superseded" && !value.supersededByRowId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "superseded register rows require supersededByRowId",
            path: ["supersededByRowId"]
        });
    }
});
export const RequirementUnresolvedRegisterRowSchema = RequirementUnresolvedProvenanceFieldsSchema.extend({
    contentFingerprint: NonEmptyStringSchema,
    sourceCrawlArtifactId: IdSchema,
    crawlLens: z.enum([
        "mechanical-review",
        "controls-bms-review",
        "scope-demarcation",
        "commercial-compliance",
        "generalist-sweep"
    ])
}).strict();
export const EngineFeedRequirementRowSchema = z
    .object({
    requirementRowId: IdSchema,
    requirement: NonEmptyStringSchema,
    verbatim: NonEmptyStringSchema.optional(),
    discipline: NonEmptyStringSchema,
    scopeTag: NonEmptyStringSchema,
    fixStage: NonEmptyStringSchema.optional(),
    obligation: NonEmptyStringSchema.optional(),
    responsibleParty: NonEmptyStringSchema.optional(),
    brandOrStandard: NonEmptyStringSchema.optional(),
    cablingNote: NonEmptyStringSchema.optional(),
    likelyExclude: z.boolean().optional(),
    excludeReason: NonEmptyStringSchema.optional(),
    pricingImpact: NonEmptyStringSchema.optional(),
    candidateF2EvidenceGroups: z.array(z.enum(["A", "B", "C", "D", "E", "F", "G", "H"])),
    sourceRefs: z.array(SourceRefSchema).min(1),
    provenance: TenderProvenanceSchema
})
    .strict();
/**
 * Why a requirement crawl blocked, attributed to the document it blocked on.
 *
 * `documentSurfaced` records whether the block leaves anything visible on that
 * document's own workspace surface. Codes that preserve a bound unresolved row
 * are surfaced; codes that drop the provider output entirely are not, and a
 * consumer must project those as an honest crawl failure.
 */
export const RequirementCrawlBlockCodeSchema = z.enum([
    "coverage_unread",
    "provider_schema_failure",
    "provider_schema_invalid",
    "provenance_unresolved",
    "crawl_lens_blocked"
]);
export const RequirementCrawlBlockSchema = z
    .object({
    docId: IdSchema,
    crawlLens: NonEmptyStringSchema,
    code: RequirementCrawlBlockCodeSchema,
    documentSurfaced: z.boolean(),
    detail: NonEmptyStringSchema
})
    .strict();
export const RequirementRegisterArtifactSchema = TenderCommonEntityFieldsSchema.extend({
    artifactId: IdSchema,
    registerVersion: z.literal(REQUIREMENT_REGISTER_VERSION),
    sourceCrawlArtifactIds: z.array(IdSchema),
    rows: z.array(RequirementRegisterRowSchema),
    unresolvedRows: z.array(RequirementUnresolvedRegisterRowSchema).default([]),
    dedupeSummary: z
        .object({
        proposedRows: NonNegativeIntegerSchema,
        acceptedRows: NonNegativeIntegerSchema,
        rejectedRows: NonNegativeIntegerSchema,
        supersededRows: NonNegativeIntegerSchema,
        provenanceUnresolvedRows: NonNegativeIntegerSchema.default(0),
        cosmeticDuplicatesSuppressed: NonNegativeIntegerSchema,
        materialRevisionsProposed: NonNegativeIntegerSchema
    })
        .strict(),
    engineFeedRows: z.array(EngineFeedRequirementRowSchema),
    coverage: z
        .object({
        includeDocIdsRead: z.array(IdSchema),
        includeDocIdsUnread: z.array(IdSchema),
        crawlLensIds: z.array(NonEmptyStringSchema)
    })
        .strict(),
    crawlBlocks: z.array(RequirementCrawlBlockSchema).default([])
}).strict();
export const RequirementCriticProposalStatusSchema = z.enum([
    "proposed",
    "accepted",
    "rejected"
]);
export const RequirementCriticProposalSchema = z
    .object({
    proposalId: IdSchema,
    kind: z.enum([
        "likely_miss",
        "duplicate",
        "misclassification",
        "weak_provenance",
        "coverage_gap",
        "contradiction"
    ]),
    status: RequirementCriticProposalStatusSchema,
    severity: z.enum(["info", "warning", "blocker"]),
    finding: NonEmptyStringSchema,
    rationale: NonEmptyStringSchema,
    suggestedAction: NonEmptyStringSchema,
    targetRowIds: z.array(IdSchema),
    sourceDocId: IdSchema.optional(),
    sourceRef: SourceRefSchema.optional(),
    candidateRequirement: NonEmptyStringSchema.optional(),
    reviewEvents: z.array(RequirementRegisterReviewEventSchema)
})
    .strict()
    .superRefine((value, ctx) => {
    if ((value.kind === "likely_miss" ||
        value.kind === "weak_provenance" ||
        value.kind === "coverage_gap") &&
        value.sourceDocId &&
        !value.sourceRef) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "critic proposals with sourceDocId require sourceRef",
            path: ["sourceRef"]
        });
    }
    if (value.sourceRef &&
        value.sourceDocId &&
        value.sourceRef.sourceId !== value.sourceDocId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "critic proposal sourceRef.sourceId must match sourceDocId",
            path: ["sourceRef", "sourceId"]
        });
    }
});
export const RequirementCriticArtifactSchema = TenderCommonEntityFieldsSchema.extend({
    artifactId: IdSchema,
    criticVersion: z.literal(REQUIREMENT_CRITIC_VERSION),
    criticRole: z.literal("independent-adversarial-requirements-critic"),
    inputRegisterArtifactId: IdSchema,
    inputCrawlArtifactIds: z.array(IdSchema),
    proposals: z.array(RequirementCriticProposalSchema),
    coverage: z
        .object({
        registerRowCount: NonNegativeIntegerSchema,
        acceptedRowCount: NonNegativeIntegerSchema,
        proposedFindingCount: NonNegativeIntegerSchema,
        docsWithNoRows: z.array(IdSchema)
    })
        .strict(),
    noAutoFix: z.literal(true)
}).strict();
/**
 * Canonical persisted render reference. Image payloads stay in object storage and
 * are resolved by `renderObjectId`; artifacts never carry data URLs or raw bytes.
 */
export const TenderTakeoffResolvedTileRefSchema = z
    .object({
    renderObjectId: IdSchema,
    docId: IdSchema,
    page: PositiveIntegerSchema,
    tileId: IdSchema.optional(),
    objectRef: TenderObjectRefSchema,
    widthPx: PositiveIntegerSchema,
    heightPx: PositiveIntegerSchema,
    region: RegionRefSchema.optional(),
    sourceRef: SourceRefSchema
})
    .strict();
export const TenderTakeoffFieldProvenanceSchema = z
    .object({
    docId: IdSchema,
    page: PositiveIntegerSchema,
    renderObjectId: IdSchema,
    tileId: IdSchema.optional(),
    region: RegionRefSchema.optional(),
    sourceQuote: NonEmptyStringSchema,
    revisionId: IdSchema,
    confidence: ConfidenceSchema,
    sourceRef: SourceRefSchema
})
    .strict();
const TenderTakeoffFieldValueSchema = z
    .object({
    value: z.unknown(),
    provenance: TenderTakeoffFieldProvenanceSchema
})
    .strict();
const TenderTakeoffReconciliationFieldDecisionSchema = z
    .object({
    field: NonEmptyStringSchema,
    decision: z.enum(["accepted", "revised", "blocked", "needs_review"]),
    winningPassId: IdSchema.optional(),
    reason: NonEmptyStringSchema,
    sourceRefs: z.array(SourceRefSchema),
    confidence: ConfidenceSchema
})
    .strict();
const TenderTakeoffReconciliationDisagreementSchema = z
    .object({
    field: NonEmptyStringSchema,
    passIds: z.array(IdSchema).min(1),
    summary: NonEmptyStringSchema,
    targetedRelookId: IdSchema.optional(),
    sourceRefs: z.array(SourceRefSchema)
})
    .strict();
const TenderTakeoffTargetedRelookTraceSchema = z
    .object({
    targetedRelookId: IdSchema,
    field: NonEmptyStringSchema,
    tileRefIds: z.array(IdSchema).min(1),
    promptIntent: NonEmptyStringSchema,
    outcome: NonEmptyStringSchema
})
    .strict();
const TenderTakeoffReconciliationSchema = z
    .object({
    fieldDecisions: z.array(TenderTakeoffReconciliationFieldDecisionSchema),
    disagreements: z.array(TenderTakeoffReconciliationDisagreementSchema),
    targetedRelookIds: z.array(IdSchema),
    targetedRelooks: z.array(TenderTakeoffTargetedRelookTraceSchema),
    reviewerFlags: z.array(NonEmptyStringSchema)
})
    .strict();
export const TenderTakeoffFieldsSchema = z
    .object({
    type_code: TenderTakeoffFieldValueSchema,
    apartment_type_no: TenderTakeoffFieldValueSchema,
    core: TenderTakeoffFieldValueSchema,
    occupancy: TenderTakeoffFieldValueSchema,
    bedrooms: TenderTakeoffFieldValueSchema,
    wet_rooms_total: TenderTakeoffFieldValueSchema,
    fcu_zones: TenderTakeoffFieldValueSchema,
    fcu_locations: TenderTakeoffFieldValueSchema,
    thermostat_positions: TenderTakeoffFieldValueSchema,
    thermostat_locations: TenderTakeoffFieldValueSchema,
    central_screen_position_drawn: TenderTakeoffFieldValueSchema,
    wet_ufh_zones: TenderTakeoffFieldValueSchema,
    corridor_ufh_zones: TenderTakeoffFieldValueSchema,
    bathroom_eufh_zones: TenderTakeoffFieldValueSchema,
    total_ufh_zones: TenderTakeoffFieldValueSchema,
    hiu_qty: TenderTakeoffFieldValueSchema,
    mvhr_qty: TenderTakeoffFieldValueSchema,
    electric_towel_rails: TenderTakeoffFieldValueSchema,
    energy_meters: TenderTakeoffFieldValueSchema,
    comfort_cooling: TenderTakeoffFieldValueSchema,
    fcu_band: TenderTakeoffFieldValueSchema,
    source_drawings: TenderTakeoffFieldValueSchema,
    evidence_notes: TenderTakeoffFieldValueSchema,
    assumptions: TenderTakeoffFieldValueSchema,
    flags_questions: TenderTakeoffFieldValueSchema,
    confidence: TenderTakeoffFieldValueSchema
})
    .strict();
export const TenderTakeoffArtifactSchema = TenderCommonEntityFieldsSchema.extend({
    artifactId: IdSchema,
    takeoffSchemaVersion: z.literal(TENDER_TAKEOFF_SCHEMA_VERSION),
    apartmentTypeId: IdSchema,
    rawLabel: NonEmptyStringSchema,
    quantity: PositiveIntegerSchema,
    drawingSetDocIds: z.array(IdSchema),
    latestRevisionResolutionId: IdSchema,
    modelId: ProviderModelIdSchema,
    passes: z.array(IdSchema),
    reconciliation: TenderTakeoffReconciliationSchema,
    drawingEvidence: z.array(LabeledDrawingEvidenceSchema).optional(),
    fields: TenderTakeoffFieldsSchema
})
    .strict()
    .superRefine((value, ctx) => {
    enforcePurePhysicalTakeoff(value.fields, ctx);
    enforceResolvableTakeoffTileRefs(value, ctx);
    enforceResolvableDrawingEvidenceRefs(value, ctx);
    enforceTakeoffCountInvariants(value.fields, ctx);
});
export const TenderTakeoffAdapterRowSchema = z
    .object({
    sourceTakeoffArtifactId: IdSchema,
    apartmentTakeoffRow: ApartmentTakeoffRowSchema,
    normalizedFrom: z.array(HeadingMapEntrySchema),
    sourceRefs: z.array(SourceRefSchema).min(1),
    warnings: z.array(TakeoffWarningSchema)
})
    .strict()
    .superRefine((value, ctx) => {
    const rawFields = value.apartmentTakeoffRow.rawFields;
    const isScheduleDocumentRow = typeof rawFields === "object" &&
        rawFields !== null &&
        !Array.isArray(rawFields) &&
        "scheduleDocument" in rawFields;
    const engineProjection = EngineSafeTakeoffProjectionSchema.safeParse(rawFields[ENGINE_SAFE_TAKEOFF_RAW_FIELDS_KEY]);
    if (!isScheduleDocumentRow && !engineProjection.success) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "drawing adapter rows must expose only the canonical engine-safe takeoff projection",
            path: ["apartmentTakeoffRow", "rawFields", ENGINE_SAFE_TAKEOFF_RAW_FIELDS_KEY]
        });
    }
    if (!isScheduleDocumentRow &&
        Object.keys(rawFields).some((key) => key !== ENGINE_SAFE_TAKEOFF_RAW_FIELDS_KEY)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "drawing adapter rawFields cannot carry values outside the canonical engine-safe takeoff projection",
            path: ["apartmentTakeoffRow", "rawFields"]
        });
    }
    if (!isScheduleDocumentRow &&
        !value.warnings.some((warning) => warning.code === "DRAWING_VISION_REQUIRES_HUMAN_REVIEW")) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "adapter rows must preserve human-review warning",
            path: ["warnings"]
        });
    }
    if (!isScheduleDocumentRow &&
        engineProjection.success &&
        (engineProjection.data.status.flags.length > 0 ||
            engineProjection.data.status.fields.some((field) => ["blocked", "needs_review", "conflict", "omitted"].includes(field.status)))) {
        addUnresolvedConflictIssue(ctx, [
            "apartmentTakeoffRow",
            "rawFields",
            ENGINE_SAFE_TAKEOFF_RAW_FIELDS_KEY,
            "status"
        ]);
    }
    if (value.warnings.some((warning) => warning.code === "TAKEOFF_RECONCILIATION_CONFLICT")) {
        addUnresolvedConflictIssue(ctx, ["warnings"]);
    }
});
export const TenderTakeoffAdapterArtifactSchema = TenderCommonEntityFieldsSchema.extend({
    artifactId: IdSchema,
    adapterVersion: z.union([
        z.literal(TENDER_TAKEOFF_ADAPTER_VERSION),
        z.literal(TENDER_SCHEDULE_ADAPTER_VERSION)
    ]),
    sourceTakeoffArtifactIds: z.array(IdSchema),
    reviewState: z.literal("needs_review"),
    rows: z.array(TenderTakeoffAdapterRowSchema),
    compareMatrixMode: z.union([
        z.literal("source_tiles_and_quotes_required"),
        z.literal("source_rows_and_quotes_required")
    ])
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.adapterVersion !== TENDER_TAKEOFF_ADAPTER_VERSION) {
        return;
    }
    for (const [index, row] of value.rows.entries()) {
        if ("scheduleDocument" in row.apartmentTakeoffRow.rawFields) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "drawing takeoff adapter artifacts cannot bypass the engine-safe projection as schedule rows",
                path: ["rows", index, "apartmentTakeoffRow", "rawFields"]
            });
        }
    }
});
export const TenderStageRunSchema = TenderCommonEntityFieldsSchema.extend({
    runId: IdSchema,
    stage: TenderStageSchema,
    fromStage: TenderStageSchema.optional(),
    attempt: PositiveIntegerSchema,
    maxAttempts: PositiveIntegerSchema,
    inputArtifactIds: z.array(IdSchema),
    outputArtifactIds: z.array(IdSchema),
    startedAt: IsoTimestampSchema,
    completedAt: IsoTimestampSchema.optional(),
    durationMs: NonNegativeIntegerSchema.optional(),
    worker: z
        .object({
        service: NonEmptyStringSchema,
        version: NonEmptyStringSchema,
        region: NonEmptyStringSchema.optional(),
        containerImageDigest: NonEmptyStringSchema.optional()
    })
        .strict(),
    geminiTraceIds: z.array(IdSchema),
    costLedgerEntryIds: z.array(IdSchema),
    progressEventIds: z.array(IdSchema),
    error: z
        .object({
        code: NonEmptyStringSchema,
        message: NonEmptyStringSchema,
        retryable: z.boolean(),
        failedDocId: IdSchema.optional(),
        failedEntryId: IdSchema.optional()
    })
        .strict()
        .optional(),
    replayPolicy: z.enum([
        "replay_from_stage",
        "replay_doc_only",
        "replay_type_only",
        "no_replay_needed"
    ])
}).strict();
export const TenderProgressEventSchema = TenderCommonEntityFieldsSchema.extend({
    eventId: IdSchema,
    runId: IdSchema,
    eventType: z.enum([
        "upload_progress",
        "finalize_requested",
        "worker_started",
        "archive_entry_scanned",
        "document_registered",
        "stage_started",
        "stage_progress",
        "agent_tool_call",
        "stage_blocked",
        "stage_failed",
        "stage_succeeded",
        "stage_superseded",
        "delete_progress"
    ]),
    docId: IdSchema.optional(),
    archiveEntryId: IdSchema.optional(),
    artifactId: IdSchema.optional(),
    message: NonEmptyStringSchema,
    counts: z.record(z.number().nonnegative()).optional(),
    agentTurnId: IdSchema.optional(),
    toolCallId: IdSchema.optional(),
    tool: NonEmptyStringSchema.optional(),
    argsSummary: NonEmptyStringSchema.optional(),
    outcome: NonEmptyStringSchema.optional(),
    elapsedMs: NonNegativeIntegerSchema.optional(),
    createdAt: IsoTimestampSchema
}).strict();
export const TenderCostLedgerEntrySchema = TenderCommonEntityFieldsSchema.extend({
    ledgerEntryId: IdSchema,
    runId: IdSchema,
    stage: TenderStageSchema,
    callClass: z.enum([
        "classification",
        "parse-native",
        "parse-relook",
        "requirement-crawl",
        "takeoff-pass",
        "takeoff-relook",
        "critic",
        "deterministic-worker"
    ]),
    provider: z.enum(["google-gemini", "openai", "deterministic"]),
    modelId: ProviderModelIdSchema.optional(),
    usageMetadataRaw: UnknownRecordSchema.optional(),
    promptTokenCount: NonNegativeIntegerSchema,
    candidatesTokenCount: NonNegativeIntegerSchema.optional(),
    responseTokenCount: NonNegativeIntegerSchema.optional(),
    totalTokenCount: NonNegativeIntegerSchema.optional(),
    cachedContentTokenCount: NonNegativeIntegerSchema.optional(),
    thoughtsTokenCount: NonNegativeIntegerSchema.optional(),
    responseId: NonEmptyStringSchema.optional(),
    generationConfig: UnknownRecordSchema.optional(),
    providerRequestFingerprint: NonEmptyStringSchema,
    pricingTableVersion: NonEmptyStringSchema,
    inputUsdPerMillion: z.number().nonnegative(),
    outputUsdPerMillion: z.number().nonnegative(),
    costUsd: z.number().nonnegative(),
    costGbp: z
        .object({
        value: z.number().nonnegative(),
        conversionSource: NonEmptyStringSchema
    })
        .strict()
        .optional(),
    workerResourceUsage: z
        .object({
        cpuMs: NonNegativeIntegerSchema.optional(),
        memoryPeakBytes: NonNegativeIntegerSchema.optional(),
        diskPeakBytes: NonNegativeIntegerSchema.optional(),
        objectBytesRead: NonNegativeIntegerSchema.optional(),
        objectBytesWritten: NonNegativeIntegerSchema.optional()
    })
        .strict()
        .optional()
}).strict();
export const TenderCleanupObjectRefSchema = z
    .object({
    bucket: NonEmptyStringSchema,
    path: NonEmptyStringSchema,
    generation: NonEmptyStringSchema,
    sizeBytes: NonNegativeIntegerSchema.optional(),
    sha256: NonEmptyStringSchema.optional(),
    contentType: NonEmptyStringSchema.optional()
})
    .strict();
export const TenderObjectCleanupOutboxEntrySchema = TenderCommonEntityFieldsSchema.extend({
    cleanupId: IdSchema,
    objectRef: TenderCleanupObjectRefSchema,
    reason: z.enum([
        "cancel_upload_delete_failed",
        "oversize_archive_finalize_rejected",
        "delete_project_cascade_failed",
        "orphan_reaper_retry"
    ]),
    status: z.enum(["pending", "running", "succeeded", "failed"]),
    attempts: NonNegativeIntegerSchema,
    lastAttemptAt: IsoTimestampSchema.optional(),
    nextAttemptAt: IsoTimestampSchema.optional(),
    lastError: NonEmptyStringSchema.optional()
}).strict();
export const TenderDeadLetterSchema = TenderCommonEntityFieldsSchema.extend({
    deadLetterId: IdSchema,
    runId: IdSchema,
    failedStage: TenderStageSchema,
    failedStatus: z.literal("failed"),
    errorCode: NonEmptyStringSchema,
    errorMessage: NonEmptyStringSchema,
    retryable: z.boolean(),
    attempt: PositiveIntegerSchema,
    maxAttempts: PositiveIntegerSchema,
    failedDocId: IdSchema.optional(),
    failedEntryId: IdSchema.optional(),
    objectRef: TenderObjectRefSchema.optional(),
    nextManualAction: NonEmptyStringSchema
}).strict();
export const ReplayRequestSchema = TenderCommonEntityFieldsSchema.extend({
    requestId: IdSchema,
    requestedBy: TenderActorSchema,
    fromStage: TenderStageSchema,
    scope: z.enum(["pack", "doc", "revision-family", "apartment-type", "crawl-lens"]),
    targetIds: z.array(IdSchema),
    reason: NonEmptyStringSchema,
    expectedCurrentArtifactRevisionIds: z.array(IdSchema),
    expectedObjectGenerations: z.array(NonEmptyStringSchema),
    overrideSetVersion: NonNegativeIntegerSchema,
    allowReuseStages: z.array(TenderStageSchema),
    invalidateStages: z.array(TenderStageSchema),
    status: z.enum([
        "queued",
        "accepted",
        "rejected_conflict",
        "running",
        "succeeded",
        "failed"
    ])
}).strict();
const PRODUCT_ALLOCATION_PATTERNS = [
    /\btouch[-_\s]?(?:4|7)\b/i,
    /\brdu\b/i,
    /\bnetmaster\b/i,
    /\btp[-_\s]?c[-_\s]?\d+/i,
    /\bio[-_\s]?com\b/i,
    /\bfcu503\b/i,
    /\brelay\s+modules?\b/i,
    /\bbom\b/i,
    /\bproduct\s*(?:allocation|part|sku)\b/i,
    /\bsku\b/i,
    /\bpart\s*(?:no|number)\b/i,
    /\bcontroller\s*allocation\b/i
];
function enforcePurePhysicalTakeoff(fields, ctx) {
    // The takeoff/specification boundary applies to the agent-generated field
    // values. Provenance is source evidence, not a product decision: drawing
    // labels such as "Touch-7 Central Interface - All Zones" must remain
    // verbatim in sourceQuote/sourceRef.quote so a reviewer can audit the
    // physical count. Scanning the whole field object used to turn that honest
    // quotation into a product allocation violation (round 3). Keep the product
    // patterns fully intact, but apply them only to each field's value.
    const findings = Object.entries(fields).flatMap(([field, candidate]) => forbiddenProductAllocationFindings(candidate.value).map((finding) => ({
        path: [field, "value", ...finding.path]
    })));
    for (const finding of findings.slice(0, 8)) {
        addPurePhysicalIssue(ctx, ["fields", ...finding.path]);
    }
}
function addPurePhysicalIssue(ctx, path) {
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "takeoff artifacts must stay pure-physical; product allocation belongs to the specification engine",
        path,
        params: { code: TENDER_TAKEOFF_PURE_PHYSICAL_ERROR_CODE }
    });
}
function addUnresolvedConflictIssue(ctx, path) {
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "engine-feed rows require accepted, resolved takeoff facts; unresolved reconciliation conflicts are not feed-eligible",
        path,
        params: { code: TENDER_TAKEOFF_UNRESOLVED_CONFLICT_ERROR_CODE }
    });
}
function addTileRefIssue(ctx, path) {
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "takeoff evidence tile refs must resolve against the project's rendered takeoff tiles",
        path,
        params: { code: TENDER_TAKEOFF_TILE_REF_ERROR_CODE }
    });
}
function addDrawingEvidenceRefIssue(ctx, path) {
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "drawing evidence document/page refs must resolve against the project's rendered drawing tiles",
        path,
        params: { code: TENDER_TAKEOFF_TILE_REF_ERROR_CODE }
    });
}
function enforceResolvableTakeoffTileRefs(artifact, ctx) {
    const metadata = artifact.provenance.metadata;
    const rawTileRefs = metadata && typeof metadata === "object" && !Array.isArray(metadata)
        ? metadata.renderedTileRefs
        : undefined;
    const parsed = z.array(TenderTakeoffResolvedTileRefSchema).safeParse(rawTileRefs);
    if (!parsed.success || parsed.data.length === 0) {
        addTileRefIssue(ctx, ["provenance", "metadata", "renderedTileRefs"]);
        return;
    }
    const refs = parsed.data;
    const refsById = new Map(refs.map((ref) => [ref.renderObjectId, ref]));
    if (refsById.size !== refs.length) {
        addTileRefIssue(ctx, ["provenance", "metadata", "renderedTileRefs"]);
    }
    for (const field of Object.keys(artifact.fields)) {
        const provenance = artifact.fields[field].provenance;
        const matchingRef = refs.find((ref) => ref.renderObjectId === provenance.renderObjectId &&
            ref.docId === provenance.docId &&
            ref.page === provenance.page &&
            (ref.tileId ?? "full") === (provenance.tileId ?? "full"));
        if (!matchingRef) {
            addTileRefIssue(ctx, ["fields", field, "provenance", "renderObjectId"]);
            continue;
        }
        if (matchingRef.sourceRef.sourceId !== provenance.sourceRef.sourceId ||
            matchingRef.sourceRef.locator !== provenance.sourceRef.locator ||
            matchingRef.sourceRef.hash !== provenance.sourceRef.hash) {
            addTileRefIssue(ctx, ["fields", field, "provenance", "sourceRef"]);
        }
    }
    const relookIds = new Set(artifact.reconciliation.targetedRelooks.map((relook) => relook.targetedRelookId));
    for (const targetedRelookId of artifact.reconciliation.targetedRelookIds) {
        if (!relookIds.has(targetedRelookId)) {
            addTileRefIssue(ctx, ["reconciliation", "targetedRelookIds"]);
        }
    }
    for (const [index, targetedRelook] of artifact.reconciliation.targetedRelooks.entries()) {
        for (const tileRefId of targetedRelook.tileRefIds) {
            if (!refsById.has(tileRefId)) {
                addTileRefIssue(ctx, [
                    "reconciliation",
                    "targetedRelooks",
                    index,
                    "tileRefIds"
                ]);
            }
        }
    }
}
function enforceResolvableDrawingEvidenceRefs(artifact, ctx) {
    const drawingEvidence = artifact.drawingEvidence ?? [];
    if (drawingEvidence.length === 0) {
        return;
    }
    const metadata = artifact.provenance.metadata;
    const rawTileRefs = metadata && typeof metadata === "object" && !Array.isArray(metadata)
        ? metadata.renderedTileRefs
        : undefined;
    const parsed = z.array(TenderTakeoffResolvedTileRefSchema).safeParse(rawTileRefs);
    const renderedPagesByDoc = new Map();
    if (parsed.success) {
        for (const tileRef of parsed.data) {
            const pages = renderedPagesByDoc.get(tileRef.docId) ?? new Set();
            pages.add(tileRef.page);
            renderedPagesByDoc.set(tileRef.docId, pages);
        }
    }
    const drawingSetDocIds = new Set(artifact.drawingSetDocIds);
    for (const [index, evidence] of drawingEvidence.entries()) {
        const provenance = evidence.provenance;
        const page = provenance.page ?? provenance.region?.page;
        const resolves = drawingSetDocIds.has(provenance.documentId) &&
            page !== undefined &&
            renderedPagesByDoc.get(provenance.documentId)?.has(page) === true;
        if (!resolves) {
            addDrawingEvidenceRefIssue(ctx, ["drawingEvidence", index, "provenance"]);
        }
    }
}
function forbiddenProductAllocationFindings(value, path = []) {
    const findings = [];
    if (typeof value === "string") {
        if (PRODUCT_ALLOCATION_PATTERNS.some((pattern) => pattern.test(value))) {
            findings.push({ path });
        }
        return findings;
    }
    if (!value || typeof value !== "object") {
        return findings;
    }
    if (Array.isArray(value)) {
        value.forEach((item, index) => {
            findings.push(...forbiddenProductAllocationFindings(item, [...path, index]));
        });
        return findings;
    }
    for (const [key, nested] of Object.entries(value)) {
        if (PRODUCT_ALLOCATION_PATTERNS.some((pattern) => pattern.test(key))) {
            findings.push({ path: [...path, key] });
        }
        findings.push(...forbiddenProductAllocationFindings(nested, [...path, key]));
    }
    return findings;
}
function enforceTakeoffCountInvariants(fields, ctx) {
    const wet = numberFieldValue(fields.wet_ufh_zones);
    const corridor = numberFieldValue(fields.corridor_ufh_zones);
    const bathroom = numberFieldValue(fields.bathroom_eufh_zones);
    const total = numberFieldValue(fields.total_ufh_zones);
    if (wet !== undefined &&
        corridor !== undefined &&
        bathroom !== undefined &&
        total !== undefined &&
        total !== wet + corridor + bathroom) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "total_ufh_zones must equal wet_ufh_zones + corridor_ufh_zones + bathroom_eufh_zones",
            path: ["fields", "total_ufh_zones", "value"]
        });
    }
    const fcuZones = numberFieldValue(fields.fcu_zones);
    const fcuBand = stringFieldValue(fields.fcu_band);
    if (fcuZones !== undefined &&
        fcuBand !== undefined &&
        !new RegExp(`^${String(fcuZones)}(?:-FCU\\b|\\b)`, "i").test(fcuBand.trim())) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "fcu_band must be derived from fcu_zones",
            path: ["fields", "fcu_band", "value"]
        });
    }
    const thermostatPositions = numberFieldValue(fields.thermostat_positions);
    if (thermostatPositions !== undefined &&
        fcuZones !== undefined &&
        thermostatPositions < fcuZones &&
        !hasExplicitThermostatException(fields)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "thermostat_positions must be greater than or equal to fcu_zones unless explicitly flagged",
            path: ["fields", "thermostat_positions", "value"]
        });
    }
}
function numberFieldValue(field) {
    return typeof field.value === "number" && Number.isFinite(field.value)
        ? field.value
        : undefined;
}
function stringFieldValue(field) {
    return typeof field.value === "string" ? field.value : undefined;
}
function hasExplicitThermostatException(fields) {
    const values = [fields.flags_questions.value, fields.assumptions.value];
    const text = JSON.stringify(values).toLowerCase();
    return (text.includes("thermostat_positions_lt_fcu_zones") ||
        text.includes("thermostat positions less than fcu"));
}
export const TenderArtifactSchema = z.union([
    ManifestArtifactSchema,
    ClassificationArtifactSchema,
    ClassificationOverrideEventSchema,
    ParseArtifactSchema,
    RequirementCrawlArtifactSchema,
    RequirementRegisterArtifactSchema,
    RequirementCriticArtifactSchema,
    TenderTakeoffArtifactSchema,
    TenderTakeoffAdapterArtifactSchema,
    ReplayRequestSchema
]);
export const TenderArchiveSafetyPolicy = {
    version: "tender-archive-safety-v1",
    rawArchiveObjectSizeBytes: 5 * 1024 * 1024 * 1024,
    nestedArchiveDepth: 3,
    archiveEntries: 10_000,
    acceptedFiles: 8_000,
    singleExtractedFileBytes: 750 * 1024 * 1024,
    totalUncompressedAcceptedBytes: 20 * 1024 * 1024 * 1024,
    perEntryCompressionRatio: 100,
    totalArchiveCompressionRatio: 30,
    normalizedPathLength: 512,
    filenameLength: 255,
    outcomes: TenderArchiveSafetyOutcomeSchema.options
};
export const TenderObjectAuthPolicy = {
    version: "tender-object-auth-v1",
    objectPathPatterns: {
        archive: "orgs/{orgId}/projects/{projectId}/tenders/{tenderId}/packs/{packId}/archives/{archiveObjectId}",
        document: "orgs/{orgId}/projects/{projectId}/tenders/{tenderId}/packs/{packId}/documents/{docObjectId}",
        render: "orgs/{orgId}/projects/{projectId}/tenders/{tenderId}/packs/{packId}/renders/{renderObjectId}",
        quarantine: "orgs/{orgId}/projects/{projectId}/tenders/{tenderId}/packs/{packId}/quarantine/{quarantineObjectId}"
    },
    signedUrlMaxMinutes: 15,
    quarantineRetentionDays: 30,
    requiresObjectGenerationLocks: true,
    publicObjectsAllowed: false
};
function sourceRefQuoteSupportsVerbatim(sourceRef, verbatim) {
    const quote = sourceRef.quote;
    if (!quote) {
        return false;
    }
    const normalizedVerbatim = normalizeEvidenceText(verbatim);
    return normalizeEvidenceSourceTextReadings(quote).some((normalizedQuote) => evidenceQuoteIsSpecific(normalizedQuote) &&
        normalizedVerbatim.length > 0 &&
        (normalizedQuote.includes(normalizedVerbatim) ||
            normalizedVerbatim.includes(normalizedQuote)));
}
const MIN_REQUIREMENT_SOURCE_QUOTE_CHARS = 24;
const MIN_REQUIREMENT_SOURCE_QUOTE_TOKENS = 4;
function evidenceQuoteIsSpecific(normalizedQuote) {
    const tokens = normalizedQuote.split(" ").filter(Boolean);
    return (normalizedQuote.length >= MIN_REQUIREMENT_SOURCE_QUOTE_CHARS &&
        tokens.length >= MIN_REQUIREMENT_SOURCE_QUOTE_TOKENS);
}
//# sourceMappingURL=tenderIntake.js.map